import { voteViewResizeMessage } from "actions/bj.postmessage";
import Axios from "axios";
import classNames from "classnames";
import { MODE_VIEW, TYPE_BATTLE, TYPE_BATTLE_DRAW, TYPE_DEFAULT } from "constant/config";
import { allowLanguage, numberCommaString } from "helpers";
import momentJalaali from "moment-jalaali";
import React, { useCallback, useEffect, useState } from "react";
import { isMobile } from "react-device-detect";
import { useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import ReactResizeDetector from "react-resize-detector";
import { useParams } from "react-router-dom";

const lang = allowLanguage();
interface Props {
	id: string;
}
type AnswerType = {
	id: number;
	answer: string;
	image: string | null;
	count: number;
	percentage: number;
	is_win: boolean;
	record: any;
};
type VoteType = {
	id: number;
	question: string;
	type: number;
	created_at: string;
	end_at: string;
	status: number;
	answers: AnswerType[];
	multiple: number;
	preview: number;
	remain: string;
	join_count: number;
	is_writer: boolean;
	is_voting: boolean;
	is_end: boolean;
};
type Inputs = {
	answer_id: number | number[];
};

interface IParams{
	id: string;
}

export default function View() {
	const { register, handleSubmit, formState, getValues } = useForm<Inputs>({ mode: "onChange" });
	const { t } = useTranslation();

	let { id } = useParams<IParams>();
	const [error, setError] = useState(false);
	const [vote, setVote] = useState<VoteType>({
		id: 0,
		question: "",
		type: 1,
		created_at: momentJalaali().format("YYYY-MM-DD HH:mm"),
		end_at: momentJalaali().format("YYYY-MM-DD HH:mm"),
		status: 1,
		multiple: 0,
		preview: 1,
		remain: "",
		answers: [],
		join_count: 0,
		is_writer: false,
		is_voting: false,
		is_end: false,
	});
	const [preview, setPreview] = useState(false);
	const [voteImage, setVoteImage] = useState('');
	const getVote = useCallback(() => {
		return Axios({
			url: `//bjapi.afreecatv.com/api/vote/${id}`,
			withCredentials: true,
		}).then(
			(response) => {
				const data = response.data;
				const filteredImages = data.answers.filter((answer : any) => answer.image !== null);
				setVote(data);
				setVoteImage(String(data.type));
				if (data.is_voting || data.is_end) {
					setPreview(true);
				}
				if (String(data.type) === TYPE_BATTLE && filteredImages.length <= 0 && !data.is_voting && !data.is_end) {
					setVoteImage(TYPE_DEFAULT);
				}
			},
			(error) => {
				setError(true);
			},
		);
	}, [id]);

	/**
	 * 배경투명하게
	 */
	useEffect(() => {
		document.body.classList.add("bgNone");
	}, []);

	/**
	 * componentDidMount
	 */
	useEffect(() => {
		getVote();
	}, [getVote]);

	/**
	 * 투표항목을 선택했는지
	 */
	const validate = useCallback(
		(e) => {
			const answerIds: number | number[] = getValues("answer_id");
			if (Array.isArray(answerIds)) {
				return answerIds.length > 0;
			} else {
				return !!answerIds;
			}
		},
		[getValues],
	);

	/**
	 * 투표하기
	 */
	const onSubmitVoting = useCallback(
		(data: Inputs) => {
			if (!data.answer_id) {
				alert(t("항목을 선택해 주세요"));
				return;
			}
			Axios({
				method: "post",
				url: `//bjapi.afreecatv.com/api/vote/voting/${id}`,
				withCredentials: true,
				data,
			}).then(
				(response) => {
					alert(t("투표에 참여 완료되었습니다."));
					getVote();
					setPreview(true);
				},
				(error) => {
					if (error.response.data.code === -10000) {
						alert(t("로그인후에 이용해주세요"));
					}
				},
			);
		},
		[getVote, id, t],
	);

	const onReVoting = useCallback(() => {
		setPreview(false);
		const filteredImages = vote.answers.filter((answer : any) => answer.image !== null);
		if (String(vote.type) === TYPE_BATTLE && filteredImages.length <= 0 && !vote.is_end) {
			setVoteImage(TYPE_DEFAULT);
		} 
	}, [vote]);

	const voteClose = useCallback(() => {
		if (window.confirm(t("투표를 종료하시겠습니까?"))) {
			return Axios({
				method: "post",
				url: `//bjapi.afreecatv.com/api/vote/${id}`,
				withCredentials: true,
				data: {
					_method: "DELETE",
				},
			}).then(
				(response) => {
					getVote();
				},
				(error) => {
					if (error.response) {
						alert(error.response.data.message);
					} else {
						alert(error.message);
					}
				},
			);
		}
	}, [getVote, id, t]);

	return (
		<ReactResizeDetector handleHeight onResize={voteViewResizeMessage(MODE_VIEW, id)}>
			<form onSubmit={handleSubmit(onSubmitVoting)}>
				<article id="vote_view" className={classNames({ vote_end: vote.is_end, "vote-mobile": isMobile, delete: error })}>
					{error ? (
						<p>
							<Trans>투표를 등록한 유저가 삭제한 투표입니다.</Trans>
							<br />
							<Trans>다른 투표에 참여해주세요.</Trans>
						</p>
					) : (
						<>
							<div className="vote_dDay">
								<div className="time">
									<p>{vote.remain}</p>
								</div>
								<div className="user_cnt">
									<p>
										<em><Trans>전체 참여자</Trans> : </em>
										<span>{numberCommaString(vote.join_count)}</span>{lang !== "ko" ? "" : t("명")}
									</p>
								</div>
							</div>

							<div className="title">
								<h3>{vote.question}</h3>
								<span className="period">
									{momentJalaali(vote.created_at).format("YYYY. MM. DD HH:mm")} ~{" "}
									{momentJalaali(vote.end_at).format("YYYY. MM. DD HH:mm")}
								</span>
							</div>

							<ul
								className={classNames("check_list", {
									battle: voteImage === TYPE_BATTLE || voteImage === TYPE_BATTLE_DRAW,
									revoting: preview && vote.is_voting && !vote.is_end,
									both_win: vote.is_end && vote.type === 2 && vote.answers[0].percentage === 50,
									draw : voteImage === TYPE_BATTLE_DRAW
								})}
							>
								{vote.answers.slice(0, vote.type === 2 ? 2 : (vote.type === 3? 3 :vote.answers.length) ).map((answer, index) => (
									<li key={index} className={classNames({ winning: answer.is_win, "draw-default":index===1 })}>
										{preview ? (
											<div className="graph_wrap">
												<div className="text">
													<div className="check">
														<input
															type={vote.multiple === 0 ? "radio" : "checkbox"}
															name="answer_id"
															id={`answer_${answer.id}`}
															value={answer.id}
															checked={!!answer.record}
															disabled={true}
														/>
														<label htmlFor={`answer_${answer.id}`}>{answer.answer}</label>
													</div>
													<span className="count">
														{numberCommaString(answer.count)}
														<em>({`${answer.percentage}%`})</em>
													</span>
												</div>
												<div className="bar">
													<span style={{ width: `${answer.percentage}%`, left: (index===1 ? `${vote.answers[index-1]?.percentage}%`:"") }}></span>
												</div>
											</div>
										) : (
											<div className="check">
												<input
													type={vote.multiple === 0 ? "radio" : "checkbox"}
													name="answer_id"
													id={`answer_${answer.id}`}
													value={answer.id}
													ref={register({ validate })}
												/>
												<label htmlFor={`answer_${answer.id}`}>{answer.answer}</label>
											</div>
										)}
										{answer.image && (
											<div className="vote-img">
												<a
													href={answer.image}
													target="_blank"
													rel="noopener noreferrer"
													className="img"
													style={{
														backgroundImage: `url(${answer.image})`,
													}}
												>
													{" "}
												</a>
											</div>
										)}
									</li>
								))}
							</ul>

							{!vote.is_end && (
								<div className="btns">
									<div>
										{!preview && (
											<button
												type="submit"
												className={classNames("btn-basic", { blue: formState.isValid })}
												disabled={!formState.isValid}
											>
												<span><Trans>투표하기</Trans></span>
											</button>
										)}
										{preview && !vote.is_voting && (
											<button type="submit" className="btn-basic blue" onClick={() => setPreview(false)}>
												<span><Trans>투표하러 가기</Trans></span>
											</button>
										)}
										{preview && vote.is_voting && (
											<button type="submit" className="btn-basic blue" onClick={onReVoting}>
												<span><Trans>다시 투표하기</Trans></span>
											</button>
										)}
										{vote.is_writer && (
											<button type="button" className="btn-basic" onClick={voteClose}>
												<span><Trans>투표 종료</Trans></span>
											</button>
										)}
									</div>
									{vote.preview === 1 && !preview && (
										<div className="preview">
											<button type="button" className="btn-basic" onClick={() => setPreview(true)}>
												<span><Trans>결과 미리보기</Trans></span>
											</button>
										</div>
									)}
								</div>
							)}
						</>
					)}
				</article>
			</form>
		</ReactResizeDetector>
	);
}
